/* PartnerHome.jsx — Exness Partners-inspired affiliate home dashboard.
 * 2-column layout:
 *   LEFT  : Balance · Partner Link · MT5 Login Details (replaces Knowledge Base)
 *   RIGHT : Tier badge + commission rates · Qualification criteria
 *   BELOW : Loyalty Program (slab targets) · Quick Reports (Profit + Registrations)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, ArrowSquareOut, Copy, QrCode, Trophy, Medal,
  ChartLineUp, UsersFour, ArrowRight, CheckCircle,
  Database, Trophy as TrophyIcon, Lock, LockOpen,
} from "@phosphor-icons/react";
import WithdrawalForm from "./WithdrawalForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function authHeaders() {
  const tok = localStorage.getItem("flux_aff_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// Determine the affiliate tier label from total directs sponsored
function tierFromDirects(total) {
  if (total >= 1000) return { label: "Dealer", color: "#FBBF24", icon: TrophyIcon };
  if (total >= 500) return { label: "Sub-dealer", color: "#A855F7", icon: Medal };
  if (total >= 4) return { label: "Affiliate", color: "#10B981", icon: Medal };
  return { label: "Member", color: "#7DD3FC", icon: Medal };
}

// Three tier thresholds for the right-column badges
const TIER_RULES = [
  { id: "affiliate",  label: "Affiliate",  threshold: 4,    sub: "4 referrals" },
  { id: "sub_dealer", label: "Sub-dealer", threshold: 500,  sub: "500 referrals" },
  { id: "dealer",     label: "Dealer",     threshold: 1000, sub: "1000 referrals" },
];

export default function PartnerHome({ profile }) {
  const [wallet, setWallet] = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [w, p] = await Promise.all([
        axios.get(`${API}/affiliate/binary/wallet`, { headers: authHeaders() }),
        axios.get(`${API}/affiliate/binary/projection`, { headers: authHeaders() }),
      ]);
      setWallet(w.data);
      setProjection(p.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading || !wallet || !projection) {
    return (
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 p-8" data-testid="partner-home-loading">
        <p className="text-zinc-500 text-sm">Loading partner dashboard…</p>
      </div>
    );
  }

  const tier = tierFromDirects(wallet.directs.total);
  const totalWithdrawn = wallet.total_withdrawn || 0;
  const referralCode = profile?.referral_code || "VAT-0000";
  const displayId = profile?.display_id || referralCode;
  const referralLink = `${window.location.origin}/affiliate/signup?ref=${referralCode}`;

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  const mt5Accounts = [
    { type: "CENT", data: profile?.mt5_accounts?.cent },
    { type: "USD", data: profile?.mt5_accounts?.usd },
  ].filter((x) => x.data && (x.data.mt5_login_id || x.data.broker_name));

  return (
    <div className="space-y-5" data-testid="partner-home">
      {/* ============== TOP ROW: 2 COLUMNS ============== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ---- LEFT COLUMN ---- */}
        <div className="space-y-5">
          {/* Balance Card */}
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6 transition hover:border-zinc-700/70" data-testid="partner-balance-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet size={18} weight="duotone" className="text-blue-400" />
                <span className="text-sm font-semibold text-zinc-200" style={{ fontFamily: "Manrope" }}>Earnings Overview</span>
                <span className="ml-2 text-[10px] text-zinc-500 font-mono" data-testid="partner-display-id">{displayId}</span>
              </div>
              <button
                data-testid="balance-withdraw-btn"
                disabled={!wallet.withdraw_enabled}
                onClick={() => {
                  if (!wallet.withdraw_enabled) return;
                  setShowWithdrawModal(true);
                }}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
                  wallet.withdraw_enabled
                    ? "bg-amber-400 text-black hover:bg-amber-300"
                    : "bg-zinc-800 text-zinc-600 cursor-not-allowed"
                }`}
              >
                <ArrowSquareOut size={16} weight="bold" />
              </button>
            </div>

            {/* 3-line earnings breakdown */}
            <div className="space-y-3" data-testid="earnings-breakdown">
              <EarningsLine
                testid="line-direct"
                label="Direct referral + Bonus"
                sub="L1 cascade — from your direct referrals"
                value={formatINR(wallet.direct_referral_income || 0)}
                color="#7DD3FC"
              />
              <EarningsLine
                testid="line-team"
                label="Learning bonus"
                sub="L2+ cascade + slab earnings"
                value={formatINR(wallet.team_sales_profit || 0)}
                color="#A855F7"
              />
            </div>

            {/* Withdrawal summary — Total withdrawn + Remaining (Available) */}
            <div className="mt-4 grid grid-cols-2 gap-3" data-testid="withdrawal-summary">
              <div className="rounded-lg border border-zinc-800/60 bg-zinc-950/40 px-3 py-3" data-testid="summary-total-withdrawn">
                <div className="flex items-center gap-1.5 mb-1">
                  <ArrowSquareOut size={11} weight="duotone" className="text-zinc-400" />
                  <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Total Withdrawn</p>
                </div>
                <p className="text-lg font-bold text-zinc-100 font-mono" style={{ fontFamily: "Manrope" }}>{formatINR(totalWithdrawn)}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">Lifetime payouts received</p>
              </div>
              <div className={`rounded-lg border px-3 py-3 ${wallet.withdraw_enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/25 bg-amber-500/5"}`} data-testid="summary-remaining">
                <div className="flex items-center gap-1.5 mb-1">
                  <Wallet size={11} weight="duotone" className={wallet.withdraw_enabled ? "text-emerald-300" : "text-amber-300"} />
                  <p className={`text-[9px] uppercase tracking-wider font-bold ${wallet.withdraw_enabled ? "text-emerald-300" : "text-amber-300"}`}>Remaining Balance</p>
                </div>
                <p className={`text-lg font-bold font-mono ${wallet.withdraw_enabled ? "text-emerald-300" : "text-amber-300"}`} style={{ fontFamily: "Manrope" }}>{formatINR(wallet.withdrawable_balance)}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">{wallet.withdraw_enabled ? "Available to withdraw" : "Locked — refer 4 to unlock"}</p>
              </div>
            </div>
          </div>

          {/* Partner Link / Code */}
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="partner-link-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <UsersFour size={18} weight="duotone" className="text-emerald-400" />
                <span className="text-sm font-semibold text-zinc-200" style={{ fontFamily: "Manrope" }}>Your Partner Link</span>
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                <span className="px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-200 font-semibold">Partner link</span>
                <span className="px-2.5 py-1 rounded-full text-zinc-500 font-semibold">Partner code</span>
              </div>
            </div>
            <div className="rounded-lg bg-zinc-950/50 border border-zinc-800/60 px-3 py-3 text-center mb-3">
              <p className="text-sm text-blue-400 underline break-all font-mono" data-testid="partner-ref-link">{referralLink}</p>
            </div>
            <div className="flex items-center justify-center gap-6">
              <button
                data-testid="partner-copy-link-btn"
                onClick={() => copyToClipboard(referralLink)}
                className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-zinc-50 transition"
              >
                <Copy size={14} /> Copy
              </button>
              <button
                data-testid="partner-qr-btn"
                onClick={() => copyToClipboard(referralLink)}
                className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-zinc-50 transition"
              >
                <QrCode size={14} /> QR code
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-3 text-center">
              Your code: <span className="font-mono text-blue-400">{referralCode}</span>
            </p>
          </div>

          {/* MT5 Login Details (replaces Knowledge Base) */}
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="partner-mt5-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Database size={18} weight="duotone" className="text-amber-400" />
                <span className="text-sm font-semibold text-zinc-200" style={{ fontFamily: "Manrope" }}>MT5 Login Details</span>
              </div>
              {mt5Accounts.length > 0 && (
                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                  {mt5Accounts.length} linked
                </span>
              )}
            </div>

            {mt5Accounts.length === 0 ? (
              <div className="text-center py-3">
                <p className="text-sm text-zinc-400">No MT5 accounts linked yet.</p>
                <p className="text-[11px] text-zinc-500 mt-1">Link an MT5 account to start copy-trading the master strategy.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="mt5-accounts-list">
                {mt5Accounts.map(({ type, data }) => (
                  <div key={type} className="rounded-lg bg-zinc-950/50 border border-zinc-800/60 p-3" data-testid={`mt5-account-${type.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-zinc-200">{type} Account</span>
                      <StatusPill status={data.status} bridge={data.bridge} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                      <DetailRow label="Broker" value={data.broker_name || "—"} />
                      <DetailRow label="Server" value={data.mt5_server || "—"} mono />
                      <DetailRow label="Login ID" value={data.mt5_login_id || "—"} mono />
                      <DetailRow label="Currency" value={type === "CENT" ? "Cent" : "USD"} />
                    </div>
                    {data?.bridge?.last_error && (
                      <p className="text-[10px] text-rose-400 mt-2 truncate" title={data.bridge.last_error}>
                        ⚠ {data.bridge.last_error}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---- RIGHT COLUMN ---- */}
        <div className="space-y-5">
          {/* Tier + Commission Card */}
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="partner-tier-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <p className="text-xl font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>{wallet.tier || tier.label}</p>
                <tier.icon size={20} weight="duotone" style={{ color: tier.color }} />
              </div>
              <button
                data-testid="partner-levels-btn"
                onClick={() => toast.message("Tier ladder: Member → Affiliate (4 refs) → Sub-dealer (500 refs) → Dealer (1000 refs)")}
                className="text-[11px] bg-amber-400 text-black font-bold px-3 py-1.5 rounded-md hover:bg-amber-300 transition flex items-center gap-1"
              >
                Partner levels <ChartLineUp size={12} weight="bold" />
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mb-4">Your level &amp; team progression</p>

            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="tier-badges">
              {TIER_RULES.map((rule) => {
                const reached = wallet.directs.total >= rule.threshold;
                return (
                  <TierBadge
                    key={rule.id}
                    testid={`tier-${rule.id}`}
                    label={rule.label}
                    sub={rule.sub}
                    reached={reached}
                  />
                );
              })}
            </div>

            <p className="text-[10px] text-zinc-500">
              Tiers progress as you grow your direct referrals. Reach each milestone to unlock new earning capacity.
            </p>
          </div>

          {/* Qualification Criteria — Withdraw qualification */}
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="qualification-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-sm font-bold text-zinc-100" style={{ fontFamily: "Manrope" }}>
                Withdraw qualification
              </p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                wallet.withdraw_enabled
                  ? "border border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                  : "border border-zinc-700 bg-zinc-800/50 text-zinc-400"
              }`} data-testid="qual-status-pill">
                {wallet.withdraw_enabled ? "QUALIFIED" : "LOCKED"}
              </span>
            </div>

            <ProgressRow
              testid="qual-directs"
              label="Direct referrals"
              current={wallet.directs.total}
              target={4}
              suffix=""
              startLabel="Locked"
              endLabel="Withdraw unlocked"
              startReached={wallet.directs.total > 0}
              endReached={wallet.directs.total >= 4}
            />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <TeamStat testid="team-stat-subdealers" label="Sub-dealers in team" value={wallet.sub_dealers_count || 0} icon={UsersFour} color="#A855F7" />
              <TeamStat testid="team-stat-dealers" label="Dealers in team" value={wallet.dealers_count || 0} icon={TrophyIcon} color="#FBBF24" />
            </div>

            <p className="text-[10px] text-zinc-500 mt-4 leading-relaxed">
              💡 {wallet.unlocks.next_milestone}
            </p>
          </div>
        </div>
      </div>

      {/* ============== LOYALTY PROGRAM (SLABS) ============== */}
      <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="loyalty-program">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Trophy size={18} weight="duotone" className="text-amber-400" />
            <p className="text-sm font-bold text-zinc-100" style={{ fontFamily: "Manrope" }}>Slab Loyalty Program</p>
          </div>
          <span className="text-[10px] text-zinc-500">Network velocity refreshes hourly</span>
        </div>
        <p className="text-xs text-zinc-400 mb-5">Hit each slab to unlock big network payouts. ₹38,400 at Slab 4 and ₹6,14,400 at Slab 8.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {projection.scenarios.map((s, idx) => (
            <div key={idx} className="rounded-lg border border-zinc-800/50 bg-zinc-950/50 p-4" data-testid={`loyalty-target-${idx + 1}`}>
              <p className="text-[10px] uppercase tracking-widest text-purple-400 mb-1">Target {idx + 1}</p>
              <p className="text-sm font-bold text-zinc-50 mb-2 leading-snug">{s.name}</p>
              <div className="flex items-center gap-1.5 mb-2">
                {wallet.directs.total >= 4 && idx === 0 ? (
                  <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                ) : (
                  <div className="w-4 h-4 rounded-full bg-zinc-800 border border-zinc-700" />
                )}
                <p className="text-xs text-zinc-300">
                  {wallet.directs.total >= 4 && idx === 0
                    ? "Target Achieved!"
                    : `${s.directs_needed} ${idx === 0 ? "directs needed" : "more signups"}`}
                </p>
              </div>
              <p className="text-lg font-bold text-emerald-400" style={{ fontFamily: "Manrope" }}>
                +{formatINR(s.projected_earnings_inr)}
              </p>
              <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">{s.explanation}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ============== QUICK REPORTS ============== */}
      <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/80 backdrop-blur-md p-6" data-testid="quick-reports">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <p className="text-sm font-bold text-zinc-100" style={{ fontFamily: "Manrope" }}>Quick reports</p>
          <span className="text-[10px] text-zinc-500 px-2.5 py-1 rounded border border-zinc-700/50">
            Last 30 days
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReportCard
            testid="report-profit"
            icon={Medal}
            iconColor="#A855F7"
            label="Total Withdrawn"
            value={formatINR(totalWithdrawn)}
            sub="Per period"
          />
          <ReportCard
            testid="report-registrations"
            icon={UsersFour}
            iconColor="#10B981"
            label="Registrations"
            value={projection.network.velocity_30d_signups}
            sub="Per period (across network)"
          />
        </div>
      </div>

      {/* Withdrawal modal — parent-controlled */}
      <WithdrawalForm
        open={showWithdrawModal}
        onOpenChange={setShowWithdrawModal}
        onUpdate={refresh}
      />
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function DetailRow({ label, value, mono }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-xs text-zinc-200 ${mono ? "font-mono" : "font-semibold"} truncate`} title={value}>{value}</p>
    </div>
  );
}

function StatusPill({ status, bridge }) {
  const hasErr = bridge?.last_error;
  const verified = status === "verified" && !hasErr;
  const className = hasErr
    ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
    : verified
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      : "border-amber-500/40 bg-amber-500/10 text-amber-300";
  const label = hasErr ? "Bridge Error" : verified ? "Active" : (status || "Pending");
  return (
    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${className}`}>{label}</span>
  );
}

function EarningsLine({ testid, label, sub, value, color, emphasis }) {
  return (
    <div data-testid={testid} className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          <p className={`text-xs font-semibold ${emphasis ? "text-zinc-100" : "text-zinc-300"}`}>{label}</p>
        </div>
        <p className="text-[10px] text-zinc-500 mt-0.5 pl-3.5">{sub}</p>
      </div>
      <p
        className={`tracking-tight whitespace-nowrap ${emphasis ? "text-2xl font-bold" : "text-base font-semibold"}`}
        style={{ fontFamily: "Manrope", color: emphasis ? color : "#E4E4E7" }}
      >
        {value}
      </p>
    </div>
  );
}

function TierBadge({ testid, label, sub, reached }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg p-3 text-center border transition-all duration-300 ${
        reached
          ? "border-emerald-500/60 bg-emerald-500/10 shadow-[0_0_18px_rgba(16,185,129,0.35)] animate-pulse-slow"
          : "border-zinc-800 bg-zinc-950/40"
      }`}
    >
      <p
        className={`text-base font-bold tracking-tight ${reached ? "text-emerald-300" : "text-zinc-400"}`}
        style={{ fontFamily: "Manrope" }}
      >
        {label}
      </p>
      <p className={`text-[10px] mt-0.5 ${reached ? "text-emerald-400/80" : "text-zinc-500"}`}>{sub}</p>
      {reached && (
        <div className="flex items-center justify-center gap-1 mt-1">
          <CheckCircle size={10} weight="fill" className="text-emerald-400" />
          <span className="text-[9px] font-bold text-emerald-400">ACTIVE</span>
        </div>
      )}
    </div>
  );
}

function TeamStat({ testid, label, value, icon: Icon, color }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-zinc-800/50 bg-zinc-950/40 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} weight="duotone" style={{ color }} />
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      </div>
      <p className="text-xl font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>{value}</p>
    </div>
  );
}

function ProgressRow({ testid, label, current, target, suffix, startLabel, endLabel, startReached, endReached }) {
  const pct = Math.min(100, (current / target) * 100);
  return (
    <div className="mb-3 last:mb-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-blue-400 underline cursor-pointer hover:text-blue-300">{label}</p>
        <p className="text-xs font-mono text-zinc-300">{current} / {target}{suffix}</p>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${endReached ? "bg-emerald-500" : "bg-amber-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className={`text-[10px] ${startReached ? "text-zinc-300" : "text-zinc-600"}`}>{startLabel}</span>
        <span className={`text-[10px] ${endReached ? "text-emerald-400 font-bold" : "text-zinc-600"}`}>{endLabel}</span>
      </div>
    </div>
  );
}

function ReportCard({ testid, icon: Icon, iconColor, label, value, sub }) {
  return (
    <div className="rounded-lg border border-zinc-800/50 bg-zinc-950/30 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="duotone" style={{ color: iconColor }} />
        <span className="text-xs font-semibold text-zinc-300">{label}</span>
      </div>
      <p className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: "Manrope" }}>{value}</p>
      <p className="text-[11px] text-zinc-500 mt-1">{sub}</p>
    </div>
  );
}
